// App.jsx — main shell. Wraps the app in <AuthGate> (Clerk-backed) so the user signs in first.
//
// Pronto redesign (Phase 2): the 13+ legacy tabs are consolidated into a 5-tab
// shell — Today · Coffee · Cook · Kitchen · Ask — rendered in the new Glass
// design system (src/components/pronto.jsx). The existing modules are unchanged
// and reached via each tab's member sub-nav chips; Phase 4 replaces each screen's
// internals with the merged Pronto screens. Everything else (auth, inbox bell,
// ⌘K search, chat drawer, podcast bar, admin gating, hash routing, #chat route)
// is preserved.
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Coffee as CoffeeIcon, ChefHat, Package, MessageCircle, Bell, Headphones,
  ChevronLeft, MoreHorizontal,
} from "lucide-react";
import { useStore } from "./hooks/useStore";
// Today is the default landing view — keep it eager so first paint is instant.
import { Today } from "./modules/Today";
// Every other tab is code-split: its chunk only downloads when the tab is opened.
// Modules are named exports, so unwrap to a default for React.lazy.
const Tonight = lazy(() => import("./modules/Tonight").then(m => ({ default: m.Tonight })));
const Pantry = lazy(() => import("./modules/Pantry").then(m => ({ default: m.Pantry })));
const Appliances = lazy(() => import("./modules/Appliances").then(m => ({ default: m.Appliances })));
const Recipes = lazy(() => import("./modules/Recipes").then(m => ({ default: m.Recipes })));
const Coffee = lazy(() => import("./modules/Coffee").then(m => ({ default: m.Coffee })));
const Inbox = lazy(() => import("./modules/Inbox").then(m => ({ default: m.Inbox })));
const Settings = lazy(() => import("./modules/Settings").then(m => ({ default: m.Settings })));
const Plan = lazy(() => import("./modules/Plan").then(m => ({ default: m.Plan })));
const Shopping = lazy(() => import("./modules/Shopping").then(m => ({ default: m.Shopping })));
const Listen = lazy(() => import("./modules/Listen").then(m => ({ default: m.Listen })));
const Aiden = lazy(() => import("./modules/Aiden").then(m => ({ default: m.Aiden })));
const Bottomless = lazy(() => import("./modules/Bottomless").then(m => ({ default: m.Bottomless })));
const Shop = lazy(() => import("./modules/Shop").then(m => ({ default: m.Shop })));
const Reset = lazy(() => import("./modules/Reset").then(m => ({ default: m.Reset })));
import { AuthGate, useCurrentUser } from "./components/Auth";
import { GlobalSearch } from "./components/GlobalSearch";
import { ChatBot, ChatFullPage } from "./components/ChatBot";
import { PodcastPlayerProvider, PodcastPlayerBar, usePodcastPlayer } from "./components/PodcastPlayer";
import { usePodcastAutoRefresh } from "./hooks/usePodcastAutoRefresh";
import { Sidebar, TabBar, Chip, Sheet, Group, GroupLabel, ListRow } from "./components/pronto";
import { MorningMode } from "./components/MorningMode";
import { Onboarding } from "./modules/Onboarding";
import { MedsTimerChip } from "./components/MedsTimerChip";
import { BrewRating } from "./components/BrewRating";
import { tabLabel, normalizeLocale, IT } from "./lib/i18n";
import { logUsage } from "./lib/usage";
import { shouldAutoRouteMorning } from "./lib/morning";

// Module registry — the code-split screens. `label` is the current (legacy) name;
// used for the member sub-nav chips until Phase 4 merges the screens.
const VIEWS = [
  { id: "today", label: "Today", component: Today },
  { id: "plan", label: "Plan", component: Plan },
  { id: "listen", label: "Listen", component: Listen },
  { id: "coffee", label: "Coffee", component: Coffee },
  // requiresAiden: the Fellow profile editor is meaningless without the
  // brewer. Hidden (and the route refused) for everyone else.
  { id: "aiden", label: "Profiles", component: Aiden, requiresAiden: true },
  { id: "bottomless", label: "Bottomless", component: Bottomless },
  { id: "tonight", label: "Tonight", component: Tonight },
  { id: "recipes", label: "Recipes", component: Recipes },
  { id: "pantry", label: "Pantry", component: Pantry },
  { id: "appliances", label: "Appliances", component: Appliances },
  { id: "shopping", label: "Shopping", component: Shopping },
  { id: "shop", label: "Shop", component: Shop },
  { id: "reset", label: "Reset", component: Reset },
  { id: "inbox", label: "Inbox", component: Inbox },
  { id: "settings", label: "Settings", component: Settings },
];

// The 5-tab IA. `home` is the module a tab lands on; `members` are the screens
// reachable from that tab (via sub-nav chips) until Phase 4 merges them. Settings
// is reached via the profile row, not a tab. `ask` is the chat (uses #chat route).
const TAB_GROUPS = [
  { id: "today", icon: Home, home: "today", members: ["today", "plan"] },
  { id: "coffee", icon: CoffeeIcon, home: "coffee", members: ["coffee", "aiden", "bottomless"] },
  { id: "cook", icon: ChefHat, home: "tonight", members: ["tonight", "recipes"] },
  { id: "kitchen", icon: Package, home: "pantry", members: ["pantry", "appliances", "shopping", "shop", "reset"] },
  { id: "listen", icon: Headphones, home: "listen", members: ["listen"] },
  { id: "ask", icon: MessageCircle, home: "ask", members: [] },
];

// The brew id from a rating deep-link (#today?rate=<id>) — how tapping the
// post-brew rating push lands the user on the rating sheet.
function parseRateParam() {
  const h = window.location.hash.slice(1);
  const q = h.indexOf("?");
  if (q === -1) return null;
  return new URLSearchParams(h.slice(q + 1)).get("rate");
}

function viewLabel(id) { return VIEWS.find(v => v.id === id)?.label || id; }
function groupForView(view) {
  return TAB_GROUPS.find(g => g.members.includes(view))?.id
    || (view === "settings" ? "today" : "today");
}

// A tab's landing screen vs. a screen pushed on top of it. Pushed screens get a
// real back affordance + title (Apple HIG); tab roots just get the ⋯ overflow.
const TAB_HOMES = TAB_GROUPS.map(g => g.home);
function isSubScreen(view) { return !TAB_HOMES.includes(view); }

// Shown briefly while a lazily-loaded tab chunk downloads.
function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <motion.div
        animate={{ opacity: [0.4, 1, 0.4] }}
        transition={{ duration: 1.6, repeat: Infinity }}
        className="w-8 h-8 rounded-xl bg-pronto-ink flex items-center justify-center"
      >
        <span className="font-round font-bold text-white text-[15px]">p</span>
      </motion.div>
    </div>
  );
}

export default function App() {
  return (
    <AuthGate>
      <PodcastPlayerProvider>
        <SignedInApp />
      </PodcastPlayerProvider>
    </AuthGate>
  );
}

function SignedInApp() {
  const { userId, email, ready } = useCurrentUser();
  const store = useStore({ userId, ready });
  const player = usePodcastPlayer();
  const locale = normalizeLocale(store.preferences?.language);
  const [view, setView] = useState(() => {
    const hash = window.location.hash.slice(1);
    const found = VIEWS.find(v => v.id === hash);
    if (!found) return "today"; // Today is the default landing tab
    // Aiden-only screens: without the brewer there is nothing to edit.
    if (found.requiresAiden && !store.gear?.hasAiden) return "coffee";
    return hash;
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // `#chat` renders the standalone full-page chat (also the "Ask" tab in Phase 2)
  // — reached from the chat drawer's expand and the Ask tab. Tracked separately so
  // it can overlay any tab without being a VIEWS entry.
  const [isChatRoute, setIsChatRoute] = useState(() => window.location.hash.slice(1) === "chat");
  // Morning Mode overlays the tab shell (like #chat) via the #morning hash.
  const [isMorningRoute, setIsMorningRoute] = useState(() => window.location.hash.slice(1) === "morning");
  // Rating deep-link (#today?rate=<id>). Captured in the initializer so the
  // hash-sync effect (which rewrites the hash to the bare view) can't strip it first.
  const [ratingBrewId, setRatingBrewId] = useState(parseRateParam);

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.slice(1);
      setIsChatRoute(h === "chat");
      setIsMorningRoute(h === "morning");
      const rate = parseRateParam();
      if (rate) setRatingBrewId(rate);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Auto-route into Morning Mode once per app open: only inside the configured
  // morning window AND when today's session is unstarted/incomplete (a completed
  // morning opens normal home). Guarded so it fires at most once per mount.
  const autoRoutedRef = useRef(false);
  useEffect(() => {
    if (store.loading || autoRoutedRef.current) return;
    autoRoutedRef.current = true;
    if (isChatRoute || isMorningRoute) return;
    if (shouldAutoRouteMorning({ prefs: store.preferences, session: store.morningSession })) {
      window.location.hash = "morning";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.loading]);

  // Keep the hash in sync with the active tab — but not while on the chat route,
  // so entering `#chat` isn't immediately overwritten by the current tab id.
  useEffect(() => { if (!isChatRoute && !isMorningRoute) window.location.hash = view; }, [view, isChatRoute, isMorningRoute]);

  // Auto-refresh runs at the shell so timers persist across tab navigation.
  usePodcastAutoRefresh({ store, player });

  // Personal usage signal (Adaptive v2) — log each screen view, fire-and-forget.
  useEffect(() => {
    if (userId && !isChatRoute) logUsage(userId, "nav", { view });
  }, [view, userId, isChatRoute]);

  const pendingInbox = (store.pantryInbox || []).filter(p => p.status !== "accepted" && p.status !== "dismissed").length
    + (store.coffeeInbox || []).filter(p => p.status !== "accepted" && p.status !== "dismissed").length
    + (store.recipeInbox || []).filter(p => p.status !== "imported" && p.status !== "dismissed").length;

  // Global search keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Tab navigation: Ask opens the full-page chat; other tabs land on their home.
  const goToTab = (tabId) => {
    if (tabId === "ask") { window.location.hash = "chat"; return; }
    const g = TAB_GROUPS.find(t => t.id === tabId);
    if (g) setView(g.home);
  };

  const ActiveView = VIEWS.find(v => v.id === view);
  const ActiveComponent = ActiveView?.component || Today;
  // On the chat route the tab bar should read as "Ask", not the tab we came from.
  const activeTab = isChatRoute ? "ask" : groupForView(view);
  const activeMembers = TAB_GROUPS.find(t => t.id === activeTab)?.members || [];
  // Chat is a tab root, not a pushed screen, so it shows ⋯ rather than a back arrow.
  const subScreen = !isChatRoute && isSubScreen(view);

  const tabs = TAB_GROUPS.map(g => ({ id: g.id, label: tabLabel(g.id, locale), icon: g.icon }));

  if (store.loading) {
    return (
      <div className="min-h-screen bg-pronto-page flex items-center justify-center">
        <motion.div
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-[10px] bg-pronto-logo flex items-center justify-center">
            <span className="font-round font-bold text-white text-[17px]">p</span>
          </div>
          <span className="font-sf text-xs uppercase tracking-[0.18em] text-pronto-text-3">loading pronto</span>
        </motion.div>
      </div>
    );
  }

  if (store.error) {
    return (
      <div className="min-h-screen bg-pronto-page flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-3xl mb-3">⚠️</div>
          <h2 className="font-round font-bold text-xl text-pronto-ink mb-2">Couldn't load Pronto</h2>
          <p className="text-sm text-pronto-text-2 mb-4">{store.error}</p>
          <p className="text-xs text-pronto-text-3">
            If this is your first sign-in, make sure you've run <code className="font-mono bg-white px-1 rounded">supabase/schema.sql</code> in your Supabase SQL editor.
          </p>
        </div>
      </div>
    );
  }

  // First run — before anything else, including Morning Mode. A brand-new user
  // has no key and no gear, so most of the app would silently do nothing; the
  // wizard is the only screen that makes sense until it is finished.
  //
  // Gated on `!store.loading` so it cannot flash during the initial fetch, when
  // preferences are still empty and every user would momentarily look new.
  if (!store.loading && store.userId && !store.preferences?.onboarded_at) {
    return <Onboarding store={store} onFinish={() => { window.location.hash = "today"; }} />;
  }

  // Morning Mode — full-screen guided routine, overlaying the tab shell. Stays
  // immersive (it has its own back + title) because it's a focused routine, not
  // a place you browse from.
  if (isMorningRoute) {
    return <MorningMode store={store} onExit={() => { window.location.hash = view || "today"; }} />;
  }

  return (
    <div className="min-h-screen bg-pronto-page font-sf flex flex-col md:flex-row md:gap-6 md:px-8 md:py-8">
      {/* Desktop sidebar */}
      <div className="hidden md:block md:sticky md:top-8 md:self-start">
        <Sidebar
          tabs={tabs}
          activeId={activeTab}
          onSelect={goToTab}
          footer={
            <div className="px-1">
              <button
                onClick={() => setView("settings")}
                className={`w-full text-left font-sf text-[13px] mb-3 ${view === "settings" ? "text-pronto-navy font-semibold" : "text-pronto-text-2"}`}
              >
                Settings{email ? ` · ${email}` : ""}
              </button>
              <div className="font-ny italic text-[12.5px] text-pronto-text-3 leading-relaxed">{IT.proverb}</div>
            </div>
          }
        />
      </div>

      {/* Mobile nav bar (Apple HIG). Pushed screens get a real back affordance
          labelled with the parent tab — answering "how did I get here?" — while
          tab roots show only the ⋯ overflow, since their large title sits below.
          The bar itself is click-through so it never blocks screen content. */}
      {!isChatRoute && (
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between gap-2 px-4 pointer-events-none
                      pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] pb-1.5">
        {subScreen ? (
          <button
            onClick={() => goToTab(activeTab)}
            aria-label={`Back to ${tabLabel(activeTab, locale)}`}
            className="pointer-events-auto flex items-center gap-0.5 -ml-2 pl-1 pr-2.5 py-1 rounded-lg
                       text-pronto-navy active:bg-black/5"
          >
            <ChevronLeft size={21} />
            <span className="font-sf text-[15px] leading-none">{tabLabel(activeTab, locale)}</span>
          </button>
        ) : <span />}
        <button
          onClick={() => setMenuOpen(true)}
          aria-label="More — settings, inbox and all screens"
          className="pointer-events-auto relative w-9 h-9 rounded-full flex items-center justify-center
                     bg-[var(--pg-6)] backdrop-blur-[24px] border border-[var(--pg-border)]
                     text-pronto-ink shadow-pronto-rest"
        >
          <MoreHorizontal size={18} />
          {pendingInbox > 0 && <span className="absolute -top-0.5 -right-0.5 w-[10px] h-[10px] rounded-full bg-pronto-warn border-2 border-[var(--p-page)]" />}
        </button>
      </div>
      )}

      {/* Desktop: floating bell */}
      <div className="hidden md:block fixed top-8 right-8 z-30">
        <NotificationBell count={pendingInbox} active={view === "inbox"} onClick={() => setView("inbox")} />
      </div>

      {!isChatRoute && (
        <main className="flex-1 w-full md:max-w-[600px] md:mx-auto px-5 md:px-0 pt-[calc(env(safe-area-inset-top,0px)+3.25rem)] md:pt-0 pb-32 md:pb-8 overflow-x-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              <Suspense fallback={<RouteFallback />}>
                <ActiveComponent store={store} onNavigate={setView} />
              </Suspense>
            </motion.div>
          </AnimatePresence>
        </main>
      )}

      {/* Full-page chat now lives *inside* the shell so the tab bar stays put —
          the Ask tab is a destination like any other, not a trapdoor. */}
      {isChatRoute && (
        <ChatFullPage store={store} onExit={() => { window.location.hash = view || "today"; }} />
      )}

      {/* Mobile bottom tab bar — z-40 so it stays above the full-page chat. */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 safe-bottom">
        <TabBar tabs={tabs} activeId={activeTab} onSelect={goToTab} />
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} store={store} onNavigate={setView} />
      <ChatBot store={store} />
      <PodcastPlayerBar store={store} />
      {/* Persistent meds-timer pill — roams every screen while the timer runs. */}
      <MedsTimerChip store={store} onOpen={() => { window.location.hash = "morning"; }} />

      {/* Profile / more menu — the single mobile affordance replacing the old
          header + member sub-nav. Everything stays reachable from here. */}
      <ProfileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        email={email}
        pendingInbox={pendingInbox}
        onNavigate={(id) => { setMenuOpen(false); setView(id); }}
        hasAiden={!!store.gear?.hasAiden}
      />

      {/* Post-brew rating sheet — opened by the +40min rating push deep-link. */}
      <RatingSheet
        brewId={ratingBrewId}
        store={store}
        onClose={() => { setRatingBrewId(null); if (parseRateParam()) window.location.hash = view || "today"; }}
      />
    </div>
  );
}

// The post-brew rating touchpoint. Shown when the rating push is tapped
// (#today?rate=<id>); writes through the same submitCheckIn as the next-morning path.
function RatingSheet({ brewId, store, onClose }) {
  const brewLog = brewId ? (store.brewLogs || []).find(l => l.id === brewId) : null;
  const bean = brewLog ? (store.beans || []).find(b => b.id === brewLog.bean_id) : null;
  return (
    <Sheet open={!!brewId} onClose={onClose}>
      <div className="p-5">
        <BrewRating
          brewLog={brewLog || { id: brewId, bean_id: bean?.id ?? null }}
          beanName={bean?.name}
          onSubmit={async (payload) => { await store.submitCheckIn(payload); onClose(); }}
        />
      </div>
    </Sheet>
  );
}

// The single mobile "everything else" surface — replaces the old app header +
// member sub-nav row. Keeps Settings, Inbox, and all sub-screens reachable while
// the screens themselves stay chrome-free to match the prototype.
function ProfileMenu({ open, onClose, email, pendingInbox, onNavigate, hasAiden = false }) {
  const jump = [
    { id: "plan", label: "Plan · this week" },
    { id: "recipes", label: "Recipe archive" },
    ...(hasAiden ? [{ id: "aiden", label: "Aiden profiles" }] : []),
    { id: "bottomless", label: "Coffee reorder · Bottomless" },
    { id: "appliances", label: "Appliances" },
    { id: "shopping", label: "Shopping" },
    { id: "shop", label: "Shop mode" },
    { id: "reset", label: "Shelf audit" },
  ];
  return (
    <Sheet open={open} onClose={onClose}>
      <div className="p-4 pb-8">
        <div className="flex items-center gap-3 px-1 mb-4">
          <div className="relative w-10 h-10 rounded-[11px] bg-pronto-logo flex items-center justify-center shadow-sm">
            <span className="font-round font-bold text-white text-[21px] leading-none tracking-[-0.04em]">p</span>
            <span className="absolute bottom-[7px] right-[8px] w-[5px] h-[5px] rounded-full bg-pronto-navy-light" />
          </div>
          <div className="min-w-0">
            <div className="font-round font-bold text-[17px] text-pronto-ink leading-none">Pronto</div>
            {email && <div className="text-[12.5px] text-pronto-text-3 truncate mt-0.5">{email}</div>}
          </div>
        </div>
        <GroupLabel className="mt-0">Settings</GroupLabel>
        <Group>
          <ListRow label="Settings" chevron onClick={() => onNavigate("settings")} />
          <ListRow label="Inbox" value={pendingInbox > 0 ? String(pendingInbox) : undefined} valueTint chevron onClick={() => onNavigate("inbox")} />
        </Group>
        <GroupLabel className="mt-4">Jump to</GroupLabel>
        <Group>
          {jump.map((j) => <ListRow key={j.id} label={j.label} chevron onClick={() => onNavigate(j.id)} />)}
        </Group>
        <div className="font-ny italic text-[12.5px] text-pronto-text-3 text-center mt-6">{IT.proverb}</div>
      </div>
    </Sheet>
  );
}

// Pronto brand lockup — the "p" monogram tile (icon seed) + wordmark.
function Brand() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative w-9 h-9 rounded-[10px] bg-pronto-logo flex items-center justify-center shadow-sm">
        <span className="font-round font-bold text-white text-[21px] leading-none tracking-[-0.04em]">p</span>
        <span className="absolute bottom-[7px] right-[8px] w-[5px] h-[5px] rounded-full bg-pronto-navy-light" />
      </div>
      <div className="font-round text-[20px] font-bold text-pronto-ink leading-none tracking-[-0.02em]">Pronto</div>
    </div>
  );
}

function NotificationBell({ count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative p-2 rounded-[10px] transition-colors ${
        active ? "text-pronto-navy bg-[rgba(63,74,99,0.12)]" : "text-pronto-text-2 hover:bg-[rgba(63,74,99,0.06)]"
      }`}
      aria-label={count > 0 ? `${count} pending in inbox` : "Inbox"}
    >
      <Bell size={20} strokeWidth={active ? 2.2 : 1.8} />
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-pronto-navy text-white text-[10px] font-semibold tabular flex items-center justify-center">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </button>
  );
}
